import React, { useState } from "react";
import { useSelector } from "react-redux";

// components
import Paginator from "../../components/Pagination";
import Spacer from "../../components/Spacer";
import Table from "../../components/TableLite";
// utils
import { getCompanyFeedbackColumns } from "./components/CompanyFeedbackCols";
import CompanyFeedbackFilters from "./components/CompanyFeedbackFilters";
import { CompanyFeedbackFilter } from "./components/CompanyFeedbackFilters";
import { DownloadCompanyFeedbackCsvButton } from "./components/DownloadCompanyFeedbackCsvButton";
import ReadableCompanyFeedbackFilterChips from "./components/ReadableCompanyFeedbackFilterChips";

// hooks
import { useSearchService, useSearchServicePrefetch } from "../../api/hooks";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import { SERVICE_FEEDBACK_COMPANY } from "../../api/services/defs/search-service";

import { getUserIsAdmin, getUserIsBasicAdmin } from "../../selectors/app";

import { Feedback } from "../UserFeedback/utils";
import { getFiltersForSearch } from "./utils";

import InfoPageTemplate from "../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
//pageTemplate
import useInfoPageTemplateQueryState from "../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../Error";
// styles
import css from "./CompanyFeedback.module.scss";

const sideNavigationItems = [{ key: "ALL_FEEDBACK", label: "All Feedback", href: "/company/feedback" }];

const LIMIT = 10;

const CompanyFeedback = () => {
	const userIsAdmin = useSelector(getUserIsAdmin);
	const userIsBasicAdmin = useSelector(getUserIsBasicAdmin);
	const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
	const { value: feedbackFeatureVisible } = useFeatureFlagQuery("ff-is-feedback-enabled", true);

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<CompanyFeedbackFilter>({
		defaultSortName: "last_updated",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});

	const { data, isLoading } = useSearchService<Feedback>({
		service: SERVICE_FEEDBACK_COMPANY,
		filters: getFiltersForSearch(debouncedSearchValue, filters),
		skip: (pageNumber - 1) * LIMIT,
		limit: LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Feedbacks",
	});

	// prefetch page before and 3 pages after
	useSearchServicePrefetch<Feedback>(
		{
			service: SERVICE_FEEDBACK_COMPANY,
			filters: getFiltersForSearch(debouncedSearchValue, filters),
			skip: (pageNumber - 2) * LIMIT,
			limit: LIMIT,
			sort,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<Feedback>(
		{
			service: SERVICE_FEEDBACK_COMPANY,
			filters: getFiltersForSearch(debouncedSearchValue, filters),
			skip: pageNumber * LIMIT,
			limit: LIMIT,
			sort,
		},
		3
	);

	const feedbacks = data?.results || [];
	const total = data?.total || 0;

	const emptyTableState = <EmptySearchTableMessage mainText="No Feedback Found" />;

	if (!(userIsAdmin || userIsBasicAdmin)) {
		return <ErrorPage code={403} />;
	}

	if (!feedbackFeatureVisible) {
		return <ErrorPage code={404} />;
	}

	const mainContent = (
		<div>
			<Table
				columns={getCompanyFeedbackColumns()}
				isLoading={isLoading}
				bodyCellClass={css.bodyCell}
				activeSort={sort.name}
				onColumnClick={(columnID, direction) => {
					setSort({ name: columnID, direction });
				}}
				rows={feedbacks || []}
				activeSortDirection={sort.direction}
				theme="vJan23"
				rowKey="_id"
				emptyChildren={emptyTableState}
			/>
			<Spacer height={2} />
			<Paginator
				pageCount={Math.ceil(total / LIMIT)}
				currentPage={pageNumber}
				onClick={(page) => setPageNumber(page)}
			/>
		</div>
	);

	const filterDrawer = (
		<CompanyFeedbackFilters
			isOpen={isFilterDrawerOpen}
			onClose={() => setIsFilterDrawerOpen(false)}
			filters={filters}
			onChange={setFilters}
		/>
	);

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			searchPlaceholder="Search Feedback..."
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => setIsFilterDrawerOpen(true)}
		/>
	);

	const actionButtons = [<DownloadCompanyFeedbackCsvButton key="download" />];

	return (
		<InfoPageTemplate
			mainContent={mainContent}
			pageTitle="Company Feedback"
			navigationLinks={sideNavigationItems}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={<ReadableCompanyFeedbackFilterChips filters={filters} setFilters={setFilters} />}
			drawer={filterDrawer}
			actionButtons={actionButtons}
			activeNavigationLinkKey={"ALL_FEEDBACK"}
		/>
	);
};

export default CompanyFeedback;
